import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '@vfuk/core-loading-spinner';
import axios from 'axios';
import RelatedArticles from '../RelatedArticles';
import SearchResults from './SearchResults';
import SearchInputSection from '../SectionSmartSearchMain/SearchInputSection';
import '../css/SectionTcsSearch.css';
import {
  KOA_API_ENDPOINT,
  OPCO_CODE,
  LANG_CODE,
} from '../../SectionAssessment/SectionAssessmentSurvey/SectionAssessmentBusinessGoals/SectionAssessmentBusinessGoals';
import ShowError, { ErrorDataTypes } from '../../SectionAssessment/ShowError';
import { navigate } from 'gatsby';
import TopSearches from '../SectionSmartSearchMain/TopSearches';
import twinxClient from '../ProtectedApiClient';
import {
  SectionSmartSearchResultProps,
  ApiDocumentListObject,
  CustomWindow
} from '../Types/SectionSmartSearchResultTypes';
import { VoteObjectType } from '../Types/AccordionDataTypes';
import { LoadingSpinnerWrapper } from './styles/SectionSmartSearchResult.styles';

const fireSmartSearchEvent = (searchKeyword: string, searchResultCount: number) => {
  try {
    // fire the utag link event
    if ((window as CustomWindow).onSmartSearchEvent) {
      (window as CustomWindow).onSmartSearchEvent!(searchKeyword, searchResultCount);
    }
  } catch (e) {
    console.log(
      'onSmartSearchEvent failed - please check the Interaction with Smart Search Script'
    );
  }
};

export const filterTags: any = (response: any) => {
  const users = response.map((o: any) => o.content[0].keyword);
  //storing all keywords in one array
  const usersCollection: any = [].concat(...users);
  //removing duplicate keywords from the above array
  const arr = usersCollection.filter(function (
    item: any,
    index: any,
    inputArray: any
  ) {
    return inputArray.indexOf(item) == index;
  });
  //removing empty strings from the above keywords array
  const filtered = arr.filter(function (el: any) {
    return el != '';
  });
  return filtered;
  //setTagList(filtered);
};

const scrollToRef = (ref: any) => window.scrollTo(0, ref.current.offsetTop);

const SectionSmartSearchResult = ({
  content,
}: SectionSmartSearchResultProps) => {
  const [category, setCategory] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [keyword, setkeyword] = useState<string>('');
  const [results, setResults] = useState<ApiDocumentListObject[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [tagResultsLoader, setTagResultsLoader] = useState<boolean>(false);
  const [tagsList, setTagList] = useState<string[]>([]);
  const [pagenationTotalPages, setPagenationTotal] = useState<number>(0);
  const [error, setError] = useState<ErrorDataTypes | null>(null);
  const [subcategory, setSubCategory] = useState<string>('');
  const [tagResults, setTagResults] = useState<ApiDocumentListObject[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [resultCount, setResultCount] = useState<number>(0);
  const myRef = useRef(null);
  const cancelTokenSource = useRef<any>();
  const [votesList, setVotesList] = useState<VoteObjectType[]>([]);
  const [LanguageErrorText, setLanguageErrorText] = useState<string>('');
  const [callCategoriesApiOnRetry, setCallCategoriesApiOnRetry] = useState<
    number | null
  >(null);

  const executeScroll = () => scrollToRef(myRef);

  const handlerFilterTags = (response: ApiDocumentListObject[]) => {
    const resultFilterTags = filterTags(response);
    setTagList(resultFilterTags);
  };

  const handlingMultiLanguageError = (response: ApiDocumentListObject[]) => {
    const languagesArray = response.map(
      (languageInResponse: ApiDocumentListObject) =>
        languageInResponse.characteristic[0].value.isLanguageMatch
    );
    const matchedArray = languagesArray.filter(
      (item: boolean) => item === true
    );
    const unMatchedArray = languagesArray.filter(
      (item: boolean) => item === false
    );
    unMatchedArray.length >= matchedArray.length &&
      setLanguageErrorText(content.searchTagText.languageErrorText);
  };

  const filterSubcategory = (response: ApiDocumentListObject[]) => {
    const filterOut = response
      .map((subcategoryObject: ApiDocumentListObject) => {
        return subcategoryObject.subcategory.toLowerCase().trim();
      })
      .reduce((prev: any, cur: any) => {
        prev[cur] = (prev[cur] || 0) + 1;
        return prev;
      }, {});
    const result = Object.keys(filterOut).reduce((a, b) =>
      filterOut[a] > filterOut[b] ? a : b
    );
    setSubCategory(result);
  };

  const getDocumentListAPI = async (
    searchText: string,
    category: string,
    reqType: string | null = '',
    tags: string | null = ''
  ) => {
    setCallCategoriesApiOnRetry(null);
    setLanguageErrorText('');
    setError(null);
    setSearchText(searchText);
    setCategory(category);
    const SearchLimit = content.searchResultLimit.toString();
    const query: any = {
      Text: searchText,
      VField: 'qa_sep',
      MinimumSimilarityScore: '0.7',
      Page: '1',
      SearchSize: SearchLimit,
      Suggestions: 'false',
      Opco: OPCO_CODE,
      Language: LANG_CODE,
    };

    if (category) {
      Object.assign(query, { Category: category });
    }

    if (tags) {
      Object.assign(query, { Tags: tags });
      setkeyword(tags);
      if (reqType !== 'vote') {
        setTagResultsLoader(true);
      }
    } else {
      setSelectedTags([]);
      if (reqType !== 'vote') {
        setResults([]);
        setTagResults([]);
      }
    }

    const params = new URLSearchParams(query).toString();
    cancelTokenSource.current = axios.CancelToken.source();
    const url = `${KOA_API_ENDPOINT}/documentlist?${params}`;

    try {
      const { data } = await twinxClient.get(
        url,
        cancelTokenSource.current.token
      );
      fireSmartSearchEvent(query.Text, data.result.length || 0);
      const errorStatus = data.result.status;
      if (!data.result.status && !data.result.fault) {
        if (data.result.length > content.paginationSliceCount) {
          const number = Math.ceil(
            data.result.length / content.paginationSliceCount
          );
          setPagenationTotal(number);
        } else {
          setPagenationTotal(1);
        }
        if (tags) {
          setTagResults(data.result);
          setResultCount(data.result.length);
          setTagResultsLoader(false);
        } else {
          setResults(data.result);
          setResultCount(data.result.length);
          await handlerFilterTags(data.result);
          handlingMultiLanguageError(data.result);
          setTagResultsLoader(false);
          setLoading(false);
        }
        filterSubcategory(data.result);
      } else {
        apiErrorHandler(errorStatus, tags);
      }
    } catch (error) {
      if (!window.navigator.onLine) {
        setError({
          errorContent: content.noInternetError,
          errorType: 'noInternetError',
        });
      } else if (error.message === undefined) {
        setError(null);
      } else {
        setError({
          errorContent: content.serverError,
          errorType: 'serverError',
        });
      }
    }
  };

  const apiErrorHandler = (errorStatus: string, tags: string) => {
    const serverError = ['500', '504', '403', '429'].indexOf(errorStatus);
    const noDataError = ['400', '404', '409'].indexOf(errorStatus);

    if (noDataError >= 0) {
      setError({ errorContent: content.noDataError, errorType: 'noData' });
    } else {
      setError({ errorContent: content.serverError, errorType: 'serverError' });
    }
    if (tags) {
      setTagResultsLoader(false);
    } else {
      setLoading(false);
    }
  };

  const getSetHistoryState = () => {
    if (window.history.state !== null) {
      executeScroll();
      setCategory(window.history.state.category);
      setSearchText(window.history.state.text);
      getDocumentListAPI(
        window.history.state.text,
        window.history.state.category
      );
    } else {
      navigate(content.searchMainPageSlug);
    }
  };

  const cancelPreviousApiRequest = () => {
    cancelTokenSource.current.cancel();
    setError(null);
  };

  const retryBtnFunction = () => {
    getDocumentListAPI(searchText, category, '', '');
    const retryAttemptCount = callCategoriesApiOnRetry
      ? callCategoriesApiOnRetry
      : 1;
    setCallCategoriesApiOnRetry(retryAttemptCount + 1);
  };

  useEffect(() => {
    getSetHistoryState();
    const sections = content.articlesSection.relatedArticlesList;
    const defaultArticles:any = sections.content[0].content.find((obj: any) =>
      obj.__typename === "ContentfulSectionElementArticleIndex"
    );
    if (defaultArticles) {
      defaultArticles.defaultArticlesList= defaultArticles.articles;
    }
  }, []);

  return (
    <div className="search-main" data-testid="list">
      <div className="search-main-div" ref={myRef}>
        <div className="spring">
          <SearchInputSection
            propSearchText={searchText}
            retry={callCategoriesApiOnRetry}
            content={content}
            category={category}
            setCategory={setCategory}
            getDocumentListAPI={getDocumentListAPI}
            cancel={cancelPreviousApiRequest}
          />
          <div>
            {isLoading ? (
              <LoadingSpinnerWrapper>
                {error ? (
                  <ShowError
                    errorContent={error.errorContent}
                    errorType={error.errorType}
                    customParam={retryBtnFunction}
                  />
                ) : (
                  <LoadingSpinner size={5} appearance="secondary" />
                )}
              </LoadingSpinnerWrapper>
            ) : (
              <SearchResults
                LanguageErrorText={LanguageErrorText}
                resultCount={resultCount}
                tagResults={tagResults}
                results={results}
                contenfuldata={content}
                tagsList={tagsList}
                questiontext={searchText}
                getDocumentListAPI={getDocumentListAPI}
                pagenationTotalPages={pagenationTotalPages}
                dropdown={category}
                error={error}
                tagResultsLoader={tagResultsLoader}
                votesList={votesList}
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
                cancel={cancelPreviousApiRequest}
                retryBtnProp={retryBtnFunction}
              />
            )}
          </div>
          {error && error.errorType === 'noData' && (
            <div>
              <hr style={{ marginTop: 20 }} />
              <TopSearches
                content={content}
                executeScroll={executeScroll}
                getDocumentListAPI={getDocumentListAPI}
                category={category}
                error={error.errorType}
                data-testid="top-search"
              />
            </div>
          )}
        </div>
      </div>
      {(error?.errorType === 'noData' || !error) && (
        <RelatedArticles
          contentfulData={content.articlesSection}
          subCategory={subcategory}
          page={'search'}
        />
      )}
    </div>
  );
};

export default SectionSmartSearchResult;
